import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  Users, UserPlus, Calendar, Search, Edit3, Trash2, Eye, X, ChevronLeft, ChevronRight,
  Activity, DollarSign, CreditCard, CheckCircle2, AlertCircle, Clock, FileText, Download,
  Upload, Bell, Briefcase, ClipboardList, Award, TrendingUp, Printer, Mail, Phone, MapPin,
  Building2, Shield, LogOut, CheckCircle, XCircle, Clock3, Send, Filter, RefreshCw,
  FileDown
} from 'lucide-react';
import { openPrintWindow, generatePayslipHTML, generateLeaveDocumentHTML } from '../utils/printUtils';

const currency = (v) => `KES ${Number(v || 0).toLocaleString()}`;
const formatDate = (v) => {
  if (!v) return '';
  try { return new Date(v).toLocaleDateString('en-KE', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return v; }
};

const StatusBadge = ({ status }) => {
  const colors = {
    'Active': 'bg-green-50 text-green-700 border-green-200',
    'On Leave': 'bg-yellow-50 text-yellow-700 border-yellow-200',
    'Suspended': 'bg-red-50 text-red-700 border-red-200',
    'Inactive': 'bg-gray-50 text-gray-500 border-gray-200',
    'Resigned': 'bg-gray-50 text-gray-500 border-gray-200',
    'Terminated': 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${colors[status] || 'bg-gray-50 text-gray-500 border-gray-200'}`}>
      {status}
    </span>
  );
};

const PayStatusBadge = ({ status }) => {
  const colors = {
    'Paid': 'bg-green-50 text-green-700 border-green-200',
    'Partial': 'bg-yellow-50 text-yellow-700 border-yellow-200',
    'Pending': 'bg-orange-50 text-orange-700 border-orange-200',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${colors[status] || 'bg-orange-50 text-orange-700 border-orange-200'}`}>
      {status}
    </span>
  );
};

const LeaveStatusBadge = ({ status }) => {
  const colors = {
    'Approved': 'bg-green-50 text-green-700 border-green-200',
    'Rejected': 'bg-red-50 text-red-700 border-red-200',
    'Pending': 'bg-yellow-50 text-yellow-700 border-yellow-200',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${colors[status] || 'bg-gray-50 text-gray-500 border-gray-200'}`}>
      {status}
    </span>
  );
};

const Staff = () => {
  const [staff, setStaff] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [filters, setFilters] = useState({ status: '', department: '', employment_type: '' });
  const [showPayForm, setShowPayForm] = useState(false);
  const [payData, setPayData] = useState({});
  const [payrollStaff, setPayrollStaff] = useState(null);
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [leaveData, setLeaveData] = useState({});
  const [showDocForm, setShowDocForm] = useState(false);
  const [docData, setDocData] = useState({});
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskData, setTaskData] = useState({});
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportType, setReportType] = useState('staff_list');
  const [reportData, setReportData] = useState([]);
  const [activeTab, setActiveTab] = useState('profile');
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [leaveApplications, setLeaveApplications] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [attendanceLog, setAttendanceLog] = useState([]);
  const [performanceReviews, setPerformanceReviews] = useState([]);
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [showLeaveDocumentModal, setShowLeaveDocumentModal] = useState(false);
  const [selectedLeaveDoc, setSelectedLeaveDoc] = useState(null);
  const [companyInfo, setCompanyInfo] = useState({});
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const perPage = 15;

  const fetchStaff = useCallback(async () => {
    try {
      setLoading(true);
      const params = { search };
      if (filters.status) params.status = filters.status;
      if (filters.department) params.department = filters.department;
      if (filters.employment_type) params.employment_type = filters.employment_type;
      const [staffRes, sumRes] = await Promise.all([
        api.get('/staff', { params }),
        api.get('/staff/summary'),
      ]);
      setStaff(staffRes.data);
      setSummary(sumRes.data);
    } catch (e) {
      toast.error('Failed to load staff data');
    } finally {
      setLoading(false);
    }
  }, [search, filters]);

  const fetchStaffDetail = async (id) => {
    try {
      const res = await api.get(`/staff/${id}`);
      setSelectedStaff(res.data);
      setActiveTab('profile');
      
      const [payroll, docs, tasksRes, leaves, attendance, reviews, notifs] = await Promise.all([
        api.get(`/staff-payroll/${id}`).catch(() => ({ data: [] })),
        api.get(`/staff-extended/documents/${id}`).catch(() => ({ data: [] })),
        api.get(`/staff-extended/tasks?staff_id=${id}`).catch(() => ({ data: [] })),
        api.get(`/staff-extended/leave?staff_id=${id}`).catch(() => ({ data: [] })),
        api.get(`/staff-extended/attendance-log?staff_id=${id}`).catch(() => ({ data: [] })),
        api.get(`/staff-extended/performance-reviews?staff_id=${id}`).catch(() => ({ data: [] })),
        api.get(`/staff-extended/notifications/${id}`).catch(() => ({ data: [] })),
      ]);
      
      setPayrollHistory(payroll.data);
      setDocuments(docs.data);
      setTasks(tasksRes.data);
      setLeaveApplications(leaves.data);
      setAttendanceLog(attendance.data);
      setPerformanceReviews(reviews.data);
      setNotifications(notifs.data);
    } catch (e) {
      toast.error('Failed to load staff details');
    }
  };

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  useEffect(() => {
    api.get('/staff-extended/branches').then(r => setBranches(r.data)).catch(() => {});
    api.get('/staff-extended/roles').then(r => setRoles(r.data)).catch(() => {});
    api.post('/staff-extended/seed-roles').catch(() => {});
    api.get('/settings').then(r => {
      const settings = r.data?.settings || r.data || {};
      setCompanyInfo({
        name: settings.clinic_name || 'NutriAxis Clinic',
        address: settings.clinic_address || 'Nairobi, Kenya',
        phone: settings.clinic_phone || '',
        whatsapp: settings.clinic_whatsapp || '',
        email: settings.clinic_email || '',
        website: settings.clinic_website || '',
        pin: settings.clinic_pin || '',
        logo: settings.clinic_logo || '/nutriaxis-logo.png'
      });
      setSettingsLoaded(true);
    }).catch(() => {
      // Set default company info if settings fetch fails
      setCompanyInfo({
        name: 'NutriAxis Clinic',
        address: 'Nairobi, Kenya',
        phone: '',
        whatsapp: '',
        email: '',
        website: '',
        pin: '',
        logo: '/nutriaxis-logo.png'
      });
      setSettingsLoaded(true);
    });
  }, []);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name}? This action cannot be undone.`)) return;
    try {
      await api.delete(`/staff/${id}`);
      toast.success('Staff deleted');
      fetchStaff();
      if (selectedStaff?.id === id) setSelectedStaff(null);
    } catch (e) {
      toast.error('Failed to delete staff');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/staff/${editingId}`, formData);
        toast.success('Staff updated');
      } else {
        await api.post('/staff', formData);
        toast.success('Staff created');
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({});
      fetchStaff();
    } catch (e) {
      toast.error('Failed to save staff');
    }
  };

  const handlePaySubmit = async (e) => {
    e.preventDefault();
    if (!payrollStaff) return;
    try {
      const grossPay = (Number(payData.salary || 0) + Number(payData.allowances || 0) + Number(payData.bonuses || 0));
      const totalDeductions = (Number(payData.deductions || 0) + Number(payData.nhif || 0) + Number(payData.nssf || 0) + Number(payData.paye || 0) + Number(payData.housing_levy || 0) + Number(payData.advances || 0) + Number(payData.loan_deductions || 0));
      const netPay = grossPay - totalDeductions;
      const amountPaid = Number(payData.amount_paid || 0);
      const balanceDue = netPay - amountPaid;
      let paymentStatus = 'Pending';
      if (amountPaid >= netPay && netPay > 0) paymentStatus = 'Paid';
      else if (amountPaid > 0) paymentStatus = 'Partial';

      const result = await api.post('/staff-extended/payroll', {
        staff_id: payrollStaff.id,
        pay_date: payData.pay_date,
        salary: payData.salary || 0,
        allowances: payData.allowances || 0,
        bonuses: payData.bonuses || 0,
        deductions: payData.deductions || 0,
        nhif: payData.nhif || 0,
        nssf: payData.nssf || 0,
        paye: payData.paye || 0,
        housing_levy: payData.housing_levy || 0,
        advances: payData.advances || 0,
        loan_deductions: payData.loan_deductions || 0,
        gross_pay: grossPay,
        total_deductions: totalDeductions,
        net_pay: netPay,
        amount_paid: amountPaid,
        balance_due: balanceDue,
        payment_status: paymentStatus,
        payment_method: payData.payment_method,
        notes: payData.notes,
      });
      
      // Show the payslip
      setSelectedPayroll(result.data);
      setShowPayslipModal(true);
      
      toast.success('Payroll processed');
      setShowPayForm(false);
      setPayData({});
      setPayrollStaff(null);
      if (payrollStaff) fetchStaffDetail(payrollStaff.id);
    } catch (e) {
      toast.error('Failed to process payroll');
    }
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStaff) return;
    try {
      const result = await api.post('/staff-extended/leave', {
        staff_id: selectedStaff.id,
        leave_type: leaveData.leave_type,
        start_date: leaveData.start_date,
        end_date: leaveData.end_date,
        reason: leaveData.reason,
      });
      
      // Show the leave document
      setSelectedLeaveDoc(result.data);
      setShowLeaveDocumentModal(true);
      
      toast.success('Leave application submitted');
      setShowLeaveForm(false);
      setLeaveData({});
      fetchStaffDetail(selectedStaff.id);
    } catch (e) {
      toast.error('Failed to apply for leave');
    }
  };

  const handleDocSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStaff) return;
    try {
      await api.post('/staff-extended/documents', {
        staff_id: selectedStaff.id,
        document_type: docData.document_type,
        document_name: docData.document_name,
        file_path: docData.file_path || docData.document_name,
      });
      toast.success('Document uploaded');
      setShowDocForm(false);
      setDocData({});
      fetchStaffDetail(selectedStaff.id);
    } catch (e) {
      toast.error('Failed to upload document');
    }
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStaff) return;
    try {
      await api.post('/staff-extended/tasks', {
        staff_id: selectedStaff.id,
        assigned_by: selectedStaff.id,
        title: taskData.title,
        description: taskData.description,
        due_date: taskData.due_date,
        priority: taskData.priority || 'Medium',
      });
      toast.success('Task assigned');
      setShowTaskForm(false);
      setTaskData({});
      fetchStaffDetail(selectedStaff.id);
    } catch (e) {
      toast.error('Failed to assign task');
    }
  };

  const handleApproveLeave = async (id, status) => {
    try {
      await api.put(`/staff-extended/leave/${id}/approve`, { status, approved_by: selectedStaff?.id, comments: '' });
      toast.success(`Leave ${status.toLowerCase()}`);
      fetchStaffDetail(selectedStaff.id);
    } catch (e) {
      toast.error('Failed to update leave');
    }
  };

  const openEdit = (s) => {
    setFormData(s);
    setEditingId(s.id);
    setShowForm(true);
  };

  const openPayForm = (s) => {
    if (!s) return;
    setPayrollStaff(s);
    setPayData({ staff_id: s.id, salary: s.salary, pay_date: new Date().toISOString().split('T')[0], amount_paid: s.salary, payment_method: 'Bank Transfer' });
    setShowPayForm(true);
  };

  const openProcessLeaveForm = () => {
    if (!selectedStaff) return;
    setLeaveData({ 
      staff_id: selectedStaff.id, 
      leave_type: '', 
      start_date: '', 
      end_date: '', 
      reason: '',
      processed: true 
    });
    setShowLeaveForm(true);
  };

  const printPayslip = (payroll) => {
    setSelectedPayroll(payroll);
    setShowPayslipModal(true);
  };

  const generateLeaveDocument = (leave) => {
    setSelectedLeaveDoc(leave);
    setShowLeaveDocumentModal(true);
  };

  const paginatedStaff = staff.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(staff.length / perPage);

  const KpiCard = ({ label, value, icon, bg }) => (
    <div className="rounded-xl bg-white border border-[#EEF2F7] p-5 shadow-[0_2px_6px_rgba(0,0,0,0.05)]">
      <div className="flex items-start justify-between mb-2">
        <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center`}>{icon}</div>
      </div>
      <p className="text-xs font-medium text-[#6B7280]">{label}</p>
      <p className="text-[22px] font-bold text-[#1F2937] mt-0.5">{value}</p>
    </div>
  );

  const generateReport = async (type) => {
    try {
      const res = await api.get(`/staff-extended/reports?type=${type}`);
      setReportData(res.data);
      setReportType(type);
      setShowReportModal(true);
    } catch (e) {
      toast.error('Failed to generate report');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[28px] font-bold text-[#1F2937]">Staff Management</h1>
          <p className="text-sm text-[#6B7280] mt-1">Complete HR management system</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => generateReport('staff_list')} className="px-3 py-2 text-sm font-semibold text-[#2563EB] border border-[#2563EB] rounded-lg hover:bg-blue-50 flex items-center gap-1">
            <Download size={16} /> Reports
          </button>
          <button onClick={() => { setEditingId(null); setFormData({}); setShowForm(true); }}
            className="bg-[#2563EB] text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#1D4ED8] flex items-center gap-2">
            <UserPlus size={18} /> Add Staff
          </button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <KpiCard label="Total Staff" value={summary.total_staff} icon={<Users size={18} className="text-[#2563EB]" />} bg="bg-[#EFF6FF]" />
          <KpiCard label="Active Staff" value={summary.active_staff} icon={<Activity size={18} className="text-[#22C55E]" />} bg="bg-[#ECFDF5]" />
          <KpiCard label="On Leave" value={summary.on_leave} icon={<Calendar size={18} className="text-[#F97316]" />} bg="bg-[#FFF7ED]" />
          <KpiCard label="Inactive" value={summary.inactive_staff} icon={<X size={18} className="text-[#EF4444]" />} bg="bg-[#FEF2F2]" />
          <KpiCard label="New This Month" value={summary.new_staff_this_month} icon={<UserPlus size={18} className="text-[#8B5CF6]" />} bg="bg-[#F5F3FF]" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, ID, email..."
            className="w-full pl-9 pr-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm bg-white" />
        </div>
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm bg-white">
          <option value="">All Status</option>
          <option>Active</option><option>On Leave</option><option>Suspended</option><option>Inactive</option>
        </select>
        <select value={filters.department} onChange={(e) => setFilters({ ...filters, department: e.target.value })}
          className="px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm bg-white">
          <option value="">All Departments</option>
          <option>Nutrition</option><option>Administration</option><option>Finance</option><option>Marketing</option><option>Laboratory</option>
        </select>
        <select value={filters.employment_type} onChange={(e) => setFilters({ ...filters, employment_type: e.target.value })}
          className="px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm bg-white">
          <option value="">All Types</option>
          <option>Full-Time</option><option>Part-Time</option><option>Contract</option><option>Intern</option>
        </select>
        {(search || filters.status || filters.department || filters.employment_type) && (
          <button onClick={() => { setSearch(''); setFilters({ status: '', department: '', employment_type: '' }); }}
            className="text-sm text-[#2563EB] font-medium">Clear</button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-[#EEF2F7] shadow-[0_2px_8px_rgba(15,23,42,0.05)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
              <thead className="bg-[#F9FAFB]">
                <tr>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-[#374151] uppercase">Staff Profile</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-[#374151] uppercase">Position/Dept</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-[#374151] uppercase">Contact</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-[#374151] uppercase">Type</th>
                  <th className="px-4 py-3.5 text-center text-xs font-semibold text-[#374151] uppercase">Salary</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-[#374151] uppercase">Status</th>
                  <th className="px-4 py-3.5 text-right text-xs font-semibold text-[#374151] uppercase">Actions</th>
                </tr>
              </thead>
            <tbody className="divide-y divide-[#F3F4F6]">
              {loading ? (
                <tr><td colSpan={7} className="px-6 py-12 text-center text-gray-400">Loading...</td></tr>
              ) : paginatedStaff.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-12 text-center text-gray-400">No staff found</td></tr>
              ) : paginatedStaff.map((s) => (
                <tr key={s.id} className="hover:bg-[#F9FAFB]">
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-3">
                      {s.profile_photo ? (
                        <img src={s.profile_photo} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-[#EEF2F7]" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] flex items-center justify-center text-white text-xs font-bold shadow-sm">
                          {s.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-[#1F2937]">{s.full_name}</p>
                        <p className="text-[11px] text-[#2563EB] font-medium">{s.staff_id}</p>
                        <button onClick={() => fetchStaffDetail(s.id)} className="text-[11px] text-[#2563EB] hover:underline font-medium mt-0.5">View Profile</button>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <p className="text-sm text-[#1F2937]">{s.position}</p>
                    <p className="text-xs text-[#6B7280]">{s.department}</p>
                  </td>
                  <td className="px-4 py-3.5">
                    <p className="text-sm text-[#6B7280]">{s.phone_number}</p>
                    <p className="text-xs text-[#9CA3AF]">{s.email}</p>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-[#6B7280]">{s.employment_type}</td>
                  <td className="px-4 py-3.5 text-center">
                    <p className="text-sm font-semibold text-[#1F2937]">{currency(s.salary)}</p>
                  </td>
                  <td className="px-4 py-3.5"><StatusBadge status={s.status} /></td>
                  <td className="px-4 py-3.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => fetchStaffDetail(s.id)} className="p-1.5 hover:bg-gray-100 rounded-lg" title="View"><Eye size={16} className="text-[#2563EB]" /></button>
                      <button onClick={() => openEdit(s)} className="p-1.5 hover:bg-gray-100 rounded-lg" title="Edit"><Edit3 size={16} className="text-[#6B7280]" /></button>
                      <button onClick={() => openPayForm(s)} className="p-1.5 hover:bg-gray-100 rounded-lg" title="Payroll"><DollarSign size={16} className="text-[#16A34A]" /></button>
                      <button onClick={() => handleDelete(s.id, s.full_name)} className="p-1.5 hover:bg-gray-100 rounded-lg" title="Delete"><Trash2 size={16} className="text-[#EF4444]" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-[#F3F4F6] flex items-center justify-between">
            <span className="text-sm text-[#6B7280]">Showing {(page - 1) * perPage + 1}-{Math.min(page * perPage, staff.length)} of {staff.length}</span>
            <div className="flex items-center gap-2">
              <button disabled={page === 1} onClick={() => setPage(page - 1)} className="p-1.5 hover:bg-gray-100 rounded-lg disabled:opacity-30"><ChevronLeft size={16} /></button>
              <span className="text-sm text-[#1F2937] font-medium">{page} / {totalPages}</span>
              <button disabled={page === totalPages} onClick={() => setPage(page + 1)} className="p-1.5 hover:bg-gray-100 rounded-lg disabled:opacity-30"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Staff Profile Modal with Tabs */}
      {selectedStaff && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-10 pb-10 overflow-y-auto" onClick={() => setSelectedStaff(null)}>
          <div className="bg-white rounded-xl w-full max-w-5xl mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#EEF2F7]">
              <h2 className="text-lg font-semibold text-[#1F2937]">Staff Profile</h2>
              <button onClick={() => setSelectedStaff(null)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            
            {/* Tabs */}
            <div className="px-6 pt-4 border-b border-[#EEF2F7] flex gap-2 overflow-x-auto">
              {[
                { id: 'profile', label: 'Profile', icon: Users },
                { id: 'payroll', label: 'Payroll', icon: DollarSign },
                { id: 'leave', label: 'Leave', icon: Calendar },
                { id: 'attendance', label: 'Attendance', icon: Clock3 },
                { id: 'tasks', label: 'Tasks', icon: ClipboardList },
                { id: 'performance', label: 'Performance', icon: Award },
                { id: 'documents', label: 'Documents', icon: FileText },
                { id: 'notifications', label: 'Notifications', icon: Bell },
              ].map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg flex items-center gap-1.5 whitespace-nowrap ${activeTab === tab.id ? 'bg-[#EFF6FF] text-[#2563EB]' : 'text-gray-600 hover:bg-gray-50'}`}>
                  <tab.icon size={16} /> {tab.label}
                </button>
              ))}
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="space-y-6">
                  <div className="flex items-center gap-5 pb-4 border-b border-[#F3F4F6]">
                    {selectedStaff.profile_photo ? (
                      <img src={selectedStaff.profile_photo} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-[#EEF2F7]" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] flex items-center justify-center text-white text-xl font-bold shadow-sm">
                        {selectedStaff.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                      </div>
                    )}
                    <div>
                      <p className="text-lg font-bold text-[#1F2937]">{selectedStaff.full_name}</p>
                      <p className="text-sm text-[#6B7280]">{selectedStaff.staff_id} · {selectedStaff.position} · {selectedStaff.department}</p>
                      <div className="mt-1"><StatusBadge status={selectedStaff.status} /></div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-semibold text-[#374151] uppercase tracking-wider mb-3">Personal Information</h3>
                      <div className="space-y-2.5">
                        <InfoRow label="Gender" value={selectedStaff.gender} />
                        <InfoRow label="Date of Birth" value={formatDate(selectedStaff.date_of_birth)} />
                        <InfoRow label="National ID" value={selectedStaff.national_id} />
                        <InfoRow label="Address" value={selectedStaff.address} />
                        <InfoRow label="Emergency Contact" value={selectedStaff.emergency_contact_name} />
                        <InfoRow label="Emergency Phone" value={selectedStaff.emergency_contact_phone} />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-[#374151] uppercase tracking-wider mb-3">Employment Information</h3>
                      <div className="space-y-2.5">
                        <InfoRow label="Department" value={selectedStaff.department} />
                        <InfoRow label="Position" value={selectedStaff.position} />
                        <InfoRow label="Employment Type" value={selectedStaff.employment_type} />
                        <InfoRow label="Date Hired" value={formatDate(selectedStaff.date_hired)} />
                        <InfoRow label="Supervisor" value={selectedStaff.supervisor} />
                        <InfoRow label="Salary" value={currency(selectedStaff.salary)} />
                        <InfoRow label="Contract Type" value={selectedStaff.contract_type} />
                        <InfoRow label="Working Hours" value={selectedStaff.working_hours} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Payroll Tab */}
              {activeTab === 'payroll' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-[#374151] uppercase tracking-wider">Payroll History</h3>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setShowPayForm(true)} className="text-xs font-semibold text-[#2563EB] hover:underline flex items-center gap-1">
                        <DollarSign size={14} /> Process Payroll
                      </button>
                    </div>
                  </div>
                  {payrollHistory.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-[#F9FAFB]">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Pay Date</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-[#374151]">Gross Pay</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-[#374151]">Deductions</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-[#374151]">Net Pay</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-[#374151]">Amount Paid</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-[#374151]">Balance</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Status</th>
                            <th className="px-3 py-2 text-center text-xs font-semibold text-[#374151]">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F3F4F6]">
                          {payrollHistory.map((p) => (
                            <tr key={p.id} className="hover:bg-[#F9FAFB] cursor-pointer" onClick={() => printPayslip(p)}>
                              <td className="px-3 py-2.5 text-[#1F2937]">{formatDate(p.pay_date)}</td>
                              <td className="px-3 py-2.5 text-right font-medium">{currency(p.gross_pay)}</td>
                              <td className="px-3 py-2.5 text-right text-[#EF4444]">{currency(p.total_deductions)}</td>
                              <td className="px-3 py-2.5 text-right font-bold text-[#1F2937]">{currency(p.net_pay)}</td>
                              <td className="px-3 py-2.5 text-right font-semibold text-[#2563EB]">{currency(p.amount_paid)}</td>
                              <td className="px-3 py-2.5 text-right font-semibold text-[#EF4444]">{currency(p.balance_due)}</td>
                              <td className="px-3 py-2.5"><PayStatusBadge status={p.payment_status} /></td>
                              <td className="px-3 py-2.5 text-center">
                                <button onClick={(e) => { e.stopPropagation(); printPayslip(p); }} className="p-1.5 hover:bg-gray-100 rounded-lg" title="Print Payslip">
                                  <Printer size={16} className="text-[#2563EB]" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 py-4 text-center">No payroll records found</p>
                  )}
                </div>
              )}

              {/* Leave Tab */}
              {activeTab === 'leave' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-[#374151] uppercase tracking-wider">Leave Applications</h3>
                    <div className="flex items-center gap-2">
                      <button onClick={openProcessLeaveForm} className="text-xs font-semibold text-[#2563EB] hover:underline flex items-center gap-1">
                        <Calendar size={14} /> Process Leave
                      </button>
                      <button onClick={() => setShowLeaveForm(true)} className="text-xs font-semibold text-[#2563EB] hover:underline flex items-center gap-1">
                        <Calendar size={14} /> Apply for Leave
                      </button>
                    </div>
                  </div>
                  {leaveApplications.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-[#F9FAFB]">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Type</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Dates</th>
                            <th className="px-3 py-2 text-center text-xs font-semibold text-[#374151]">Days</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Status</th>
                            <th className="px-3 py-2 text-center text-xs font-semibold text-[#374151]">Approval</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F3F4F6]">
                          {leaveApplications.map((l) => (
                            <tr key={l.id} className="hover:bg-[#F9FAFB] cursor-pointer" onClick={() => generateLeaveDocument(l)}>
                              <td className="px-3 py-2.5 font-medium">{l.leave_type}</td>
                              <td className="px-3 py-2.5 text-[#6B7280]">{formatDate(l.start_date)} - {formatDate(l.end_date)}</td>
                              <td className="px-3 py-2.5 text-center">{l.days_taken}</td>
                              <td className="px-3 py-2.5"><LeaveStatusBadge status={l.status} /></td>
                              <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                                {l.status === 'Pending' ? (
                                  <div className="flex items-center justify-center gap-2">
                                    <button onClick={() => handleApproveLeave(l.id, 'Approved')} className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 text-xs font-semibold">
                                      <CheckCircle size={14} /> Approve
                                    </button>
                                    <button onClick={() => handleApproveLeave(l.id, 'Rejected')} className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 text-xs font-semibold">
                                      <XCircle size={14} /> Reject
                                    </button>
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                                <button onClick={() => generateLeaveDocument(l)} className="p-1.5 hover:bg-gray-100 rounded-lg" title="Print Leave Document">
                                  <Printer size={16} className="text-[#2563EB]" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 py-4 text-center">No leave applications found</p>
                  )}
                </div>
              )}

              {/* Attendance Tab */}
              {activeTab === 'attendance' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-[#374151] uppercase tracking-wider">Attendance Log</h3>
                  {attendanceLog.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-[#F9FAFB]">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Date</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Clock In</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Clock Out</th>
                            <th className="px-3 py-2 text-center text-xs font-semibold text-[#374151]">Hours</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F3F4F6]">
                          {attendanceLog.map((a) => (
                            <tr key={a.id} className="hover:bg-[#F9FAFB]">
                              <td className="px-3 py-2.5">{formatDate(a.date)}</td>
                              <td className="px-3 py-2.5">{a.clock_in ? new Date(a.clock_in).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                              <td className="px-3 py-2.5">{a.clock_out ? new Date(a.clock_out).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                              <td className="px-3 py-2.5 text-center">{a.hours_worked || '—'}</td>
                              <td className="px-3 py-2.5"><StatusBadge status={a.status} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 py-4 text-center">No attendance records found</p>
                  )}
                </div>
              )}

              {/* Tasks Tab */}
              {activeTab === 'tasks' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-[#374151] uppercase tracking-wider">Assigned Tasks</h3>
                    <button onClick={() => setShowTaskForm(true)} className="text-xs font-semibold text-[#2563EB] hover:underline flex items-center gap-1">
                      <ClipboardList size={14} /> Assign Task
                    </button>
                  </div>
                  {tasks.length > 0 ? (
                    <div className="space-y-2">
                      {tasks.map((t) => (
                        <div key={t.id} className="p-3 bg-[#F9FAFB] rounded-lg border border-[#EEF2F7] flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-[#1F2937]">{t.title}</p>
                            <p className="text-xs text-[#6B7280]">{t.description}</p>
                            <p className="text-xs text-[#9CA3AF] mt-1">Due: {formatDate(t.due_date)} · Priority: {t.priority}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${t.status === 'Completed' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>{t.status}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 py-4 text-center">No tasks assigned</p>
                  )}
                </div>
              )}

              {/* Performance Tab */}
              {activeTab === 'performance' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-[#374151] uppercase tracking-wider">Performance Reviews</h3>
                  {performanceReviews.length > 0 ? (
                    <div className="space-y-3">
                      {performanceReviews.map((r) => (
                        <div key={r.id} className="p-4 bg-[#F9FAFB] rounded-lg border border-[#EEF2F7]">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-medium text-[#1F2937]">Review by {r.reviewer_name}</p>
                            <span className="text-xs text-[#6B7280]">{formatDate(r.review_date)}</span>
                          </div>
                          <p className="text-sm text-[#6B7280]">Rating: <span className="font-semibold">{r.overall_rating}/5</span></p>
                          {r.strengths && <p className="text-sm text-[#6B7280] mt-1"><strong>Strengths:</strong> {r.strengths}</p>}
                          {r.areas_for_improvement && <p className="text-sm text-[#6B7280] mt-1"><strong>Improvement Areas:</strong> {r.areas_for_improvement}</p>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 py-4 text-center">No performance reviews found</p>
                  )}
                </div>
              )}

              {/* Documents Tab */}
              {activeTab === 'documents' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-[#374151] uppercase tracking-wider">Documents</h3>
                    <button onClick={() => setShowDocForm(true)} className="text-xs font-semibold text-[#2563EB] hover:underline flex items-center gap-1">
                      <Upload size={14} /> Upload Document
                    </button>
                  </div>
                  {documents.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {documents.map((d) => (
                        <div key={d.id} className="p-3 bg-[#F9FAFB] rounded-lg border border-[#EEF2F7] flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FileText size={18} className="text-[#2563EB]" />
                            <div>
                              <p className="text-sm font-medium text-[#1F2937]">{d.document_name}</p>
                              <p className="text-xs text-[#6B7280]">{d.document_type}</p>
                            </div>
                          </div>
                          <button onClick={async () => { await api.delete(`/staff-extended/documents/${d.id}`); fetchStaffDetail(selectedStaff.id); toast.success('Document deleted'); }} className="p-1.5 hover:bg-gray-200 rounded" title="Delete">
                            <Trash2 size={14} className="text-red-600" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileText size={48} className="mx-auto text-gray-300 mb-2" />
                      <p className="text-sm text-gray-400">No documents uploaded</p>
                      <p className="text-xs text-gray-400 mt-1">Upload Employment Contract, CV, Academic Certificates, National ID, KRA PIN, NSSF, NHIF/SHIF, Passport Photo, Driving License, or other documents</p>
                    </div>
                  )}
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-[#374151] uppercase tracking-wider">Notifications</h3>
                  {notifications.length > 0 ? (
                    <div className="space-y-2">
                      {notifications.map((n) => (
                        <div key={n.id} className={`p-3 rounded-lg border ${n.is_read ? 'bg-gray-50 border-gray-200' : 'bg-blue-50 border-blue-200'}`}>
                          <p className="text-sm font-medium text-[#1F2937]">{n.title}</p>
                          <p className="text-xs text-[#6B7280] mt-1">{n.message}</p>
                          <p className="text-xs text-[#9CA3AF] mt-1">{formatDate(n.created_at)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 py-4 text-center">No notifications</p>
                  )}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-[#EEF2F7] flex items-center justify-end gap-3">
              <button onClick={() => { setSelectedStaff(null); openEdit(selectedStaff); }} className="px-4 py-2 text-sm font-semibold text-[#2563EB] border border-[#2563EB] rounded-lg hover:bg-blue-50">Edit Staff</button>
              <button onClick={() => setSelectedStaff(null)} className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-10 pb-10 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-xl w-full max-w-2xl mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#EEF2F7]">
              <h2 className="text-lg font-semibold text-[#1F2937]">{editingId ? 'Edit Staff' : 'Add New Staff'}</h2>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Full Name" required value={formData.full_name} onChange={(v) => setFormData({ ...formData, full_name: v })} />
                <FormField label="Gender" type="select" options={['', 'Male', 'Female', 'Other']} value={formData.gender} onChange={(v) => setFormData({ ...formData, gender: v })} />
                <FormField label="Date of Birth" type="date" value={formData.date_of_birth?.split('T')[0]} onChange={(v) => setFormData({ ...formData, date_of_birth: v })} />
                <FormField label="National ID" value={formData.national_id} onChange={(v) => setFormData({ ...formData, national_id: v })} />
                <FormField label="Phone Number" required value={formData.phone_number} onChange={(v) => setFormData({ ...formData, phone_number: v })} />
                <FormField label="Email" type="email" value={formData.email} onChange={(v) => setFormData({ ...formData, email: v })} />
                <FormField label="Position" required value={formData.position} onChange={(v) => setFormData({ ...formData, position: v })} />
                <FormField label="Department" required type="select" options={['', 'Nutrition', 'Administration', 'Finance', 'Marketing', 'Laboratory']} value={formData.department} onChange={(v) => setFormData({ ...formData, department: v })} />
                <FormField label="Employment Type" type="select" options={['', 'Full-Time', 'Part-Time', 'Contract', 'Intern']} value={formData.employment_type} onChange={(v) => setFormData({ ...formData, employment_type: v })} />
                <FormField label="Date Hired" type="date" value={formData.date_hired?.split('T')[0]} onChange={(v) => setFormData({ ...formData, date_hired: v })} />
                <FormField label="Supervisor" value={formData.supervisor} onChange={(v) => setFormData({ ...formData, supervisor: v })} />
                <FormField label="Salary (KES)" type="number" value={formData.salary} onChange={(v) => setFormData({ ...formData, salary: v })} />
                <FormField label="Contract Type" value={formData.contract_type} onChange={(v) => setFormData({ ...formData, contract_type: v })} />
                <FormField label="Working Hours" value={formData.working_hours} onChange={(v) => setFormData({ ...formData, working_hours: v })} />
                <FormField label="Status" type="select" options={['Active', 'On Leave', 'Suspended', 'Inactive']} value={formData.status} onChange={(v) => setFormData({ ...formData, status: v })} />
                <FormField label="Address" value={formData.address} onChange={(v) => setFormData({ ...formData, address: v })} />
              </div>
              <div className="pt-4 border-t border-[#EEF2F7] flex items-center justify-end gap-3">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-6 py-2 text-sm font-semibold text-white bg-[#2563EB] rounded-lg hover:bg-[#1D4ED8]">{editingId ? 'Update' : 'Create'} Staff</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Record Payroll Modal */}
      {showPayForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-10 overflow-y-auto" onClick={() => { setShowPayForm(false); setPayrollStaff(null); }}>
          <div className="bg-white rounded-xl w-full max-w-xl mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#EEF2F7]">
              <h2 className="text-lg font-semibold text-[#1F2937]"><span className="flex items-center gap-2"><DollarSign size={18} className="text-[#16A34A]" /> Process Payroll</span></h2>
              <button onClick={() => { setShowPayForm(false); setPayrollStaff(null); }} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <form onSubmit={handlePaySubmit} className="p-6 space-y-4">
              {payrollStaff && (
                <div className="flex items-center gap-3 pb-3 border-b border-[#F3F4F6]">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] flex items-center justify-center text-white text-xs font-bold">
                    {payrollStaff.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </div>
                  <div>
                    <p className="font-semibold text-[#1F2937]">{payrollStaff.full_name}</p>
                    <p className="text-xs text-[#6B7280]">{payrollStaff.staff_id} · Basic Salary: {currency(payrollStaff.salary)}</p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Pay Date" type="date" required value={payData.pay_date} onChange={(v) => setPayData({ ...payData, pay_date: v })} />
                <FormField label="Basic Salary (KES)" type="number" value={payData.salary} onChange={(v) => setPayData({ ...payData, salary: v })} />
                <FormField label="Allowances (KES)" type="number" value={payData.allowances} onChange={(v) => setPayData({ ...payData, allowances: v })} />
                <FormField label="Bonuses (KES)" type="number" value={payData.bonuses} onChange={(v) => setPayData({ ...payData, bonuses: v })} />
                <FormField label="NHIF (KES)" type="number" value={payData.nhif} onChange={(v) => setPayData({ ...payData, nhif: v })} />
                <FormField label="NSSF (KES)" type="number" value={payData.nssf} onChange={(v) => setPayData({ ...payData, nssf: v })} />
                <FormField label="PAYE (KES)" type="number" value={payData.paye} onChange={(v) => setPayData({ ...payData, paye: v })} />
                <FormField label="Housing Levy (KES)" type="number" value={payData.housing_levy} onChange={(v) => setPayData({ ...payData, housing_levy: v })} />
                <FormField label="Advances (KES)" type="number" value={payData.advances} onChange={(v) => setPayData({ ...payData, advances: v })} />
                <FormField label="Loan Deductions (KES)" type="number" value={payData.loan_deductions} onChange={(v) => setPayData({ ...payData, loan_deductions: v })} />
                <FormField label="Other Deductions (KES)" type="number" value={payData.deductions} onChange={(v) => setPayData({ ...payData, deductions: v })} />
                <FormField label="Amount Paid (KES)" type="number" required value={payData.amount_paid} onChange={(v) => setPayData({ ...payData, amount_paid: v })} />
                <FormField label="Payment Method" type="select" options={['Bank Transfer', 'Cash', 'Cheque', 'M-Pesa']} value={payData.payment_method} onChange={(v) => setPayData({ ...payData, payment_method: v })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Notes</label>
                <textarea value={payData.notes || ''} onChange={(e) => setPayData({ ...payData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm focus:ring-2 focus:ring-blue-200" rows={2} />
              </div>
              {(() => {
                const gross = Number(payData.salary || 0) + Number(payData.allowances || 0) + Number(payData.bonuses || 0);
                const totalDed = Number(payData.deductions || 0) + Number(payData.nhif || 0) + Number(payData.nssf || 0) + Number(payData.paye || 0) + Number(payData.housing_levy || 0) + Number(payData.advances || 0) + Number(payData.loan_deductions || 0);
                const net = gross - totalDed;
                const balance = net - Number(payData.amount_paid || 0);
                return (
                  <div className="pt-3 border-t border-[#EEF2F7] flex items-center justify-between">
                    <div className="text-sm text-[#6B7280]">
                      <div>Gross Pay: <span className="font-bold text-[#1F2937]">{currency(gross)}</span></div>
                      <div>Net Pay: <span className="font-bold text-[#1F2937]">{currency(net)}</span></div>
                      <div>Balance: <span className="font-bold text-[#EF4444]">{currency(balance)}</span></div>
                    </div>
                    <div className="flex gap-3">
                      <button type="button" onClick={() => setShowPayForm(false)} className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
                      <button type="submit" className="px-6 py-2 text-sm font-semibold text-white bg-[#16A34A] rounded-lg hover:bg-[#15803D]">Process Payroll</button>
                    </div>
                  </div>
                );
              })()}
            </form>
          </div>
        </div>
      )}

      {/* Leave Application Modal */}
      {showLeaveForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-10 overflow-y-auto" onClick={() => setShowLeaveForm(false)}>
          <div className="bg-white rounded-xl w-full max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#EEF2F7]">
              <h2 className="text-lg font-semibold text-[#1F2937]"><span className="flex items-center gap-2"><Calendar size={18} className="text-[#2563EB]" /> Apply for Leave</span></h2>
              <button onClick={() => setShowLeaveForm(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <form onSubmit={handleLeaveSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <FormField label="Leave Type" type="select" options={['', 'Annual Leave', 'Sick Leave', 'Maternity Leave', 'Paternity Leave', 'Compassionate Leave', 'Unpaid Leave']} value={leaveData.leave_type} onChange={(v) => setLeaveData({ ...leaveData, leave_type: v })} />
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Start Date" type="date" required value={leaveData.start_date} onChange={(v) => setLeaveData({ ...leaveData, start_date: v })} />
                  <FormField label="End Date" type="date" required value={leaveData.end_date} onChange={(v) => setLeaveData({ ...leaveData, end_date: v })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Reason</label>
                  <textarea value={leaveData.reason || ''} onChange={(e) => setLeaveData({ ...leaveData, reason: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm focus:ring-2 focus:ring-blue-200" rows={3} />
                </div>
              </div>
              <div className="pt-4 border-t border-[#EEF2F7] flex items-center justify-end gap-3">
                <button type="button" onClick={() => setShowLeaveForm(false)} className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-6 py-2 text-sm font-semibold text-white bg-[#2563EB] rounded-lg hover:bg-[#1D4ED8]">Submit Application</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {showDocForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-20 overflow-y-auto" onClick={() => setShowDocForm(false)}>
          <div className="bg-white rounded-xl w-full max-w-lg mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#EEF2F7]">
              <h2 className="text-lg font-semibold text-[#1F2937]"><span className="flex items-center gap-2"><Upload size={18} className="text-[#2563EB]" /> Upload Document</span></h2>
              <button onClick={() => setShowDocForm(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <form onSubmit={handleDocSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <FormField label="Document Type" type="select" options={['', 'Employment Contract', 'CV', 'Academic Certificates', 'National ID', 'KRA PIN', 'NSSF', 'NHIF/SHIF', 'Passport Photo', 'Driving License', 'Other Documents']} value={docData.document_type} onChange={(v) => setDocData({ ...docData, document_type: v })} />
                <FormField label="Document Name" required value={docData.document_name} onChange={(v) => setDocData({ ...docData, document_name: v })} />
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Upload File</label>
                  <input 
                    type="file" 
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setDocData({ ...docData, file_path: file.name, file: file });
                      }
                    }}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm focus:ring-2 focus:ring-blue-200"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                  <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, DOC, DOCX, JPG, PNG</p>
                </div>
              </div>
              <div className="pt-4 border-t border-[#EEF2F7] flex items-center justify-end gap-3">
                <button type="button" onClick={() => setShowDocForm(false)} className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-6 py-2 text-sm font-semibold text-white bg-[#2563EB] rounded-lg hover:bg-[#1D4ED8]">Upload</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Task Assignment Modal */}
      {showTaskForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-20 overflow-y-auto" onClick={() => setShowTaskForm(false)}>
          <div className="bg-white rounded-xl w-full max-w-lg mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#EEF2F7]">
              <h2 className="text-lg font-semibold text-[#1F2937]"><span className="flex items-center gap-2"><ClipboardList size={18} className="text-[#2563EB]" /> Assign Task</span></h2>
              <button onClick={() => setShowTaskForm(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <form onSubmit={handleTaskSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <FormField label="Task Title" required value={taskData.title} onChange={(v) => setTaskData({ ...taskData, title: v })} />
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Description</label>
                  <textarea value={taskData.description || ''} onChange={(e) => setTaskData({ ...taskData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm focus:ring-2 focus:ring-blue-200" rows={3} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Due Date" type="date" value={taskData.due_date} onChange={(v) => setTaskData({ ...taskData, due_date: v })} />
                  <FormField label="Priority" type="select" options={['', 'Low', 'Medium', 'High', 'Urgent']} value={taskData.priority} onChange={(v) => setTaskData({ ...taskData, priority: v })} />
                </div>
              </div>
              <div className="pt-4 border-t border-[#EEF2F7] flex items-center justify-end gap-3">
                <button type="button" onClick={() => setShowTaskForm(false)} className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-6 py-2 text-sm font-semibold text-white bg-[#2563EB] rounded-lg hover:bg-[#1D4ED8]">Assign Task</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reports Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-20 overflow-y-auto" onClick={() => setShowReportModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-4xl mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#EEF2F7]">
              <h2 className="text-lg font-semibold text-[#1F2937]"><span className="flex items-center gap-2"><FileText size={18} className="text-[#2563EB]" /> {reportType.replace(/_/g, ' ').toUpperCase()} Report</span></h2>
              <div className="flex items-center gap-2">
                <button onClick={() => window.print()} className="px-3 py-1.5 text-sm font-semibold text-[#2563EB] border border-[#2563EB] rounded-lg hover:bg-blue-50 flex items-center gap-1">
                  <Printer size={16} /> Print
                </button>
                <button onClick={() => setShowReportModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
              </div>
            </div>
            <div className="p-6 overflow-x-auto max-h-[70vh]">
              <table className="w-full text-sm">
                <thead className="bg-[#F9FAFB]">
                  <tr>
                    {reportType === 'staff_list' && <>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Staff ID</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Name</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Department</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Position</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Branch</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Status</th>
                    </>}
                    {reportType === 'attendance' && <>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Staff ID</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Name</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-[#374151]">Days Present</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-[#374151]">Total Hours</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-[#374151]">Avg Hours</th>
                    </>}
                    {reportType === 'payroll' && <>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Staff</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Pay Date</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-[#374151]">Gross</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-[#374151]">Net</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-[#374151]">Paid</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Status</th>
                    </>}
                    {reportType === 'leave' && <>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Staff</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Type</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Dates</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-[#374151]">Days</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Status</th>
                    </>}
                    {reportType === 'department' && <>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[#374151]">Department</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-[#374151]">Total</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-[#374151]">Active</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-[#374151]">On Leave</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-[#374151]">Inactive</th>
                    </>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F3F4F6]">
                  {reportData.map((row, i) => (
                    <tr key={i} className="hover:bg-[#F9FAFB]">
                      {reportType === 'staff_list' && <>
                        <td className="px-3 py-2.5 text-[#2563EB] font-medium">{row.staff_id}</td>
                        <td className="px-3 py-2.5">{row.full_name}</td>
                        <td className="px-3 py-2.5">{row.department}</td>
                        <td className="px-3 py-2.5">{row.position}</td>
                        <td className="px-3 py-2.5">{row.branch_name || '—'}</td>
                        <td className="px-3 py-2.5"><StatusBadge status={row.status} /></td>
                      </>}
                      {reportType === 'attendance' && <>
                        <td className="px-3 py-2.5 text-[#2563EB] font-medium">{row.staff_id}</td>
                        <td className="px-3 py-2.5">{row.full_name}</td>
                        <td className="px-3 py-2.5 text-center">{row.days_present}</td>
                        <td className="px-3 py-2.5 text-center">{row.total_hours}</td>
                        <td className="px-3 py-2.5 text-center">{row.avg_hours}</td>
                      </>}
                      {reportType === 'payroll' && <>
                        <td className="px-3 py-2.5">{row.full_name}</td>
                        <td className="px-3 py-2.5">{formatDate(row.pay_date)}</td>
                        <td className="px-3 py-2.5 text-right">{currency(row.gross_pay)}</td>
                        <td className="px-3 py-2.5 text-right font-bold">{currency(row.net_pay)}</td>
                        <td className="px-3 py-2.5 text-right">{currency(row.amount_paid)}</td>
                        <td className="px-3 py-2.5"><PayStatusBadge status={row.payment_status} /></td>
                      </>}
                      {reportType === 'leave' && <>
                        <td className="px-3 py-2.5">{row.full_name}</td>
                        <td className="px-3 py-2.5">{row.leave_type}</td>
                        <td className="px-3 py-2.5">{formatDate(row.start_date)} - {formatDate(row.end_date)}</td>
                        <td className="px-3 py-2.5 text-center">{row.days_taken}</td>
                        <td className="px-3 py-2.5"><LeaveStatusBadge status={row.status} /></td>
                      </>}
                      {reportType === 'department' && <>
                        <td className="px-3 py-2.5 font-medium">{row.department}</td>
                        <td className="px-3 py-2.5 text-center">{row.total}</td>
                        <td className="px-3 py-2.5 text-center text-green-600">{row.active}</td>
                        <td className="px-3 py-2.5 text-center text-yellow-600">{row.on_leave}</td>
                        <td className="px-3 py-2.5 text-center text-red-600">{row.inactive}</td>
                      </>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Payslip Modal */}
      {showPayslipModal && selectedPayroll && selectedStaff && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setShowPayslipModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#EEF2F7] flex-shrink-0">
              <h2 className="text-lg font-semibold text-[#1F2937]">Payslip</h2>
              <div className="flex items-center gap-2">
                <button onClick={() => {
                  const html = generatePayslipHTML(companyInfo, selectedStaff, selectedPayroll);
                  openPrintWindow(html);
                }} className="px-3 py-1.5 text-sm font-semibold text-white bg-[#2563EB] rounded-lg hover:bg-[#1D4ED8] flex items-center gap-1">
                  <FileDown size={16} /> Download / Print
                </button>
                <button onClick={() => setShowPayslipModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
              </div>
            </div>
            <div className="overflow-y-auto p-6" id="payslip-content">
              <div className="text-center border-b-2 border-[#2563EB] pb-4 mb-6">
                {companyInfo.logo && <img src={companyInfo.logo} alt="Company Logo" className="h-16 mx-auto mb-3 object-contain" />}
                <h1 className="text-2xl font-bold text-[#1F2937]">{companyInfo.name}</h1>
                <p className="text-sm text-[#6B7280]">{companyInfo.address}</p>
                {companyInfo.phone && <p className="text-sm text-[#6B7280]">Tel: {companyInfo.phone}</p>}
                {companyInfo.whatsapp && <p className="text-sm text-[#6B7280]">WhatsApp: {companyInfo.whatsapp}</p>}
                {companyInfo.email && <p className="text-sm text-[#6B7280]">Email: {companyInfo.email}</p>}
                {companyInfo.website && <p className="text-sm text-[#6B7280]">Web: {companyInfo.website}</p>}
                {companyInfo.pin && <p className="text-sm text-[#6B7280]">KRA PIN: {companyInfo.pin}</p>}
              </div>
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-[#1F2937]">PAYSLIP</h2>
                <p className="text-sm text-[#6B7280]">Pay Period: {formatDate(selectedPayroll.pay_date)}</p>
              </div>
              <div className="bg-[#F9FAFB] rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-[#374151] uppercase tracking-wider mb-3">Employee Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs text-[#6B7280]">Employee Name</p><p className="text-sm font-semibold text-[#1F2937]">{selectedStaff.full_name}</p></div>
                  <div><p className="text-xs text-[#6B7280]">Employee ID</p><p className="text-sm font-semibold text-[#1F2937]">{selectedStaff.staff_id}</p></div>
                  <div><p className="text-xs text-[#6B7280]">Department</p><p className="text-sm font-semibold text-[#1F2937]">{selectedStaff.department}</p></div>
                  <div><p className="text-xs text-[#6B7280]">Position</p><p className="text-sm font-semibold text-[#1F2937]">{selectedStaff.position}</p></div>
                  <div><p className="text-xs text-[#6B7280]">Employment Type</p><p className="text-sm font-semibold text-[#1F2937]">{selectedStaff.employment_type}</p></div>
                  <div><p className="text-xs text-[#6B7280]">Payment Method</p><p className="text-sm font-semibold text-[#1F2937]">{selectedPayroll.payment_method || 'Bank Transfer'}</p></div>
                </div>
              </div>
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-[#374151] uppercase tracking-wider mb-3">Earnings</h3>
                <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
                  <div className="bg-[#F9FAFB] px-4 py-2 flex justify-between">
                    <span className="text-sm font-medium text-[#374151]">Basic Salary</span>
                    <span className="text-sm font-semibold text-[#1F2937]">{currency(selectedPayroll.salary)}</span>
                  </div>
                  {Number(selectedPayroll.allowances) > 0 && <div className="px-4 py-2 flex justify-between border-t border-[#E5E7EB]"><span className="text-sm font-medium text-[#374151]">Allowances</span><span className="text-sm font-semibold text-[#1F2937]">{currency(selectedPayroll.allowances)}</span></div>}
                  {Number(selectedPayroll.bonuses) > 0 && <div className="px-4 py-2 flex justify-between border-t border-[#E5E7EB]"><span className="text-sm font-medium text-[#374151]">Bonuses</span><span className="text-sm font-semibold text-[#1F2937]">{currency(selectedPayroll.bonuses)}</span></div>}
                  <div className="bg-[#EFF6FF] px-4 py-3 flex justify-between border-t-2 border-[#2563EB]">
                    <span className="text-sm font-bold text-[#2563EB]">Gross Pay</span>
                    <span className="text-sm font-bold text-[#2563EB]">{currency(selectedPayroll.gross_pay)}</span>
                  </div>
                </div>
              </div>
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-[#374151] uppercase tracking-wider mb-3">Deductions</h3>
                <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
                  {Number(selectedPayroll.nhif) > 0 && <div className="px-4 py-2 flex justify-between border-b border-[#E5E7EB]"><span className="text-sm font-medium text-[#374151]">NHIF</span><span className="text-sm font-semibold text-[#EF4444]">{currency(selectedPayroll.nhif)}</span></div>}
                  {Number(selectedPayroll.nssf) > 0 && <div className="px-4 py-2 flex justify-between border-b border-[#E5E7EB]"><span className="text-sm font-medium text-[#374151]">NSSF</span><span className="text-sm font-semibold text-[#EF4444]">{currency(selectedPayroll.nssf)}</span></div>}
                  {Number(selectedPayroll.paye) > 0 && <div className="px-4 py-2 flex justify-between border-b border-[#E5E7EB]"><span className="text-sm font-medium text-[#374151]">PAYE</span><span className="text-sm font-semibold text-[#EF4444]">{currency(selectedPayroll.paye)}</span></div>}
                  {Number(selectedPayroll.housing_levy) > 0 && <div className="px-4 py-2 flex justify-between border-b border-[#E5E7EB]"><span className="text-sm font-medium text-[#374151]">Housing Levy</span><span className="text-sm font-semibold text-[#EF4444]">{currency(selectedPayroll.housing_levy)}</span></div>}
                  {Number(selectedPayroll.advances) > 0 && <div className="px-4 py-2 flex justify-between border-b border-[#E5E7EB]"><span className="text-sm font-medium text-[#374151]">Advances</span><span className="text-sm font-semibold text-[#EF4444]">{currency(selectedPayroll.advances)}</span></div>}
                  {Number(selectedPayroll.loan_deductions) > 0 && <div className="px-4 py-2 flex justify-between border-b border-[#E5E7EB]"><span className="text-sm font-medium text-[#374151]">Loan Deductions</span><span className="text-sm font-semibold text-[#EF4444]">{currency(selectedPayroll.loan_deductions)}</span></div>}
                  {Number(selectedPayroll.deductions) > 0 && <div className="px-4 py-2 flex justify-between border-b border-[#E5E7EB]"><span className="text-sm font-medium text-[#374151]">Other Deductions</span><span className="text-sm font-semibold text-[#EF4444]">{currency(selectedPayroll.deductions)}</span></div>}
                  <div className="bg-[#FEF2F2] px-4 py-2 flex justify-between border-t border-[#E5E7EB]">
                    <span className="text-sm font-bold text-[#EF4444]">Total Deductions</span>
                    <span className="text-sm font-bold text-[#EF4444]">{currency(selectedPayroll.total_deductions)}</span>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center">
                  <div><p className="text-sm text-white/80">Net Pay</p><p className="text-2xl font-bold text-white">{currency(selectedPayroll.net_pay)}</p></div>
                  <div className="text-right">
                    <p className="text-sm text-white/80">Amount Paid</p>
                    <p className="text-lg font-semibold text-white">{currency(selectedPayroll.amount_paid)}</p>
                    {Number(selectedPayroll.balance_due) > 0 && <p className="text-sm text-white/80">Balance: {currency(selectedPayroll.balance_due)}</p>}
                  </div>
                </div>
              </div>
              <div className="text-center text-xs text-[#6B7280] border-t border-[#E5E7EB] pt-4">
                <p>This is a computer-generated payslip and does not require a signature.</p>
                <p className="mt-1">Generated on {new Date().toLocaleDateString('en-KE', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leave Document Modal */}
      {showLeaveDocumentModal && selectedLeaveDoc && selectedStaff && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setShowLeaveDocumentModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#EEF2F7] flex-shrink-0">
              <h2 className="text-lg font-semibold text-[#1F2937]">Leave Approval Document</h2>
              <div className="flex items-center gap-2">
                <button onClick={() => {
                  const html = generateLeaveDocumentHTML(companyInfo, selectedStaff, selectedLeaveDoc);
                  openPrintWindow(html);
                }} className="px-3 py-1.5 text-sm font-semibold text-white bg-[#2563EB] rounded-lg hover:bg-[#1D4ED8] flex items-center gap-1">
                  <FileDown size={16} /> Print / Save PDF
                </button>
                <button onClick={() => setShowLeaveDocumentModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
              </div>
            </div>
            <div className="overflow-y-auto p-6" id="leave-document-content">
              <div className="text-center border-b-2 border-[#2563EB] pb-4 mb-6">
                {companyInfo.logo && <img src={companyInfo.logo} alt="Company Logo" className="h-16 mx-auto mb-3 object-contain" />}
                <h1 className="text-2xl font-bold text-[#1F2937]">{companyInfo.name}</h1>
                <p className="text-sm text-[#6B7280]">{companyInfo.address}</p>
                {companyInfo.phone && <p className="text-sm text-[#6B7280]">Tel: {companyInfo.phone}</p>}
                {companyInfo.whatsapp && <p className="text-sm text-[#6B7280]">WhatsApp: {companyInfo.whatsapp}</p>}
                {companyInfo.email && <p className="text-sm text-[#6B7280]">Email: {companyInfo.email}</p>}
                {companyInfo.website && <p className="text-sm text-[#6B7280]">Web: {companyInfo.website}</p>}
              </div>
              
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-[#1F2937]">LEAVE APPROVAL LETTER</h2>
                <p className="text-sm text-[#6B7280]">Date: {new Date().toLocaleDateString('en-KE', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>

              <div className="bg-[#F9FAFB] rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-[#374151] uppercase tracking-wider mb-3">Employee Information</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs text-[#6B7280]">Employee Name</p><p className="text-sm font-semibold text-[#1F2937]">{selectedStaff.full_name}</p></div>
                  <div><p className="text-xs text-[#6B7280]">Employee ID</p><p className="text-sm font-semibold text-[#1F2937]">{selectedStaff.staff_id}</p></div>
                  <div><p className="text-xs text-[#6B7280]">Department</p><p className="text-sm font-semibold text-[#1F2937]">{selectedStaff.department}</p></div>
                  <div><p className="text-xs text-[#6B7280]">Position</p><p className="text-sm font-semibold text-[#1F2937]">{selectedStaff.position}</p></div>
                </div>
              </div>

              <div className="bg-[#F9FAFB] rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-[#374151] uppercase tracking-wider mb-3">Leave Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs text-[#6B7280]">Leave Type</p><p className="text-sm font-semibold text-[#1F2937]">{selectedLeaveDoc.leave_type}</p></div>
                  <div><p className="text-xs text-[#6B7280]">Duration</p><p className="text-sm font-semibold text-[#1F2937]">{selectedLeaveDoc.days_taken} days</p></div>
                  <div><p className="text-xs text-[#6B7280]">Start Date</p><p className="text-sm font-semibold text-[#1F2937]">{formatDate(selectedLeaveDoc.start_date)}</p></div>
                  <div><p className="text-xs text-[#6B7280]">End Date</p><p className="text-sm font-semibold text-[#1F2937]">{formatDate(selectedLeaveDoc.end_date)}</p></div>
                  {selectedLeaveDoc.reason && <div className="col-span-2"><p className="text-xs text-[#6B7280]">Reason</p><p className="text-sm font-semibold text-[#1F2937]">{selectedLeaveDoc.reason}</p></div>}
                </div>
              </div>

              <div className="bg-[#EFF6FF] rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-[#374151] uppercase tracking-wider mb-3">Approval Status</h3>
                <div className="flex items-center gap-2">
                  <div className={`px-3 py-1.5 rounded-full text-sm font-semibold ${selectedLeaveDoc.status === 'Approved' ? 'bg-green-100 text-green-700' : selectedLeaveDoc.status === 'Rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {selectedLeaveDoc.status}
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-sm font-semibold text-[#374151] uppercase tracking-wider mb-4">Signatures</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs text-[#6B7280] mb-1">Employee Signature</p>
                    <div className="border-b-2 border-[#374151] h-12 mb-2"></div>
                    <p className="text-xs text-[#6B7280]">Date: _______________</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#6B7280] mb-1">HR Manager Signature</p>
                    <div className="border-b-2 border-[#374151] h-12 mb-2"></div>
                    <p className="text-xs text-[#6B7280]">Date: _______________</p>
                  </div>
                </div>
              </div>

              <div className="text-center text-xs text-[#6B7280] border-t border-[#E5E7EB] pt-4">
                <p>This is an official leave approval document from {companyInfo.name}.</p>
                <p className="mt-1">Generated on {new Date().toLocaleDateString('en-KE', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #payslip-content, #payslip-content * { visibility: visible; }
          #payslip-content { position: absolute; left: 0; top: 0; width: 100%; visibility: visible !important; }
          #leave-document-content, #leave-document-content * { visibility: visible; }
          #leave-document-content { position: absolute; left: 0; top: 0; width: 100%; visibility: visible !important; }
          .fixed.inset-0 { display: none !important; }
          .bg-black\\/40 { display: none !important; }
        }
      `}</style>
    </div>
  );
};

const InfoRow = ({ label, value, className = '' }) => (
  <div className={className}>
    <p className="text-xs text-[#6B7280]">{label}</p>
    <p className="text-sm font-medium text-[#1F2937]">{value || '—'}</p>
  </div>
);

const FormField = ({ label, required, type = 'text', options, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-[#374151] mb-1">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
    {type === 'select' ? (
      <select value={value || ''} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-200">
        {options.map((o) => <option key={o} value={o}>{o || `Select ${label}...`}</option>)}
      </select>
    ) : (
      <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm focus:ring-2 focus:ring-blue-200" />
    )}
  </div>
);

export default Staff;